import React, {useContext, useEffect, useState} from 'react';
import { gql } from 'apollo-boost';
import { useMutation } from '@apollo/react-hooks';
import './FisaAccountClosure.scss';
import {customerDataContext} from '../../../contexts/CustomerDataProvider';
import {useUnifiedAnalytics} from '../../../contexts/AnalyticsWrapper';
import CloseButton from "../../SharedComponents/CloseButton3";
import Loading from "../../Loading/Loading";
import {useNavigate} from "react-router";
import logoImg from "../../../assets/images/investec-logo.svg";
import successImg from "../../../assets/images/success.png";
import withFeatureFlags from "../../FeatureFlags/withFeatureFlags";
import {FisaAccountDetailsBox} from "./FISA/FisaAccountDetailsBox";
import FisaCoolingOffClosureStepOne from './FISA/FisaCoolingOffClosureStepOne';
import FisaCoolingOffClosureStepTwo from './FISA/FisaCoolingOffClosureStepTwo';
import FisaPenaltyNoticeStepsOrchestrator from './FISA/Penalty/FisaPenaltyNoticeStepsOrchestrator';
import SmsOtp from '../../Session/SmsOtp';
import Button from '../../Form/Button/Button';
import Modal from '../../Modal/Modal';

/** @import { Account } from '../../../contexts/CustomerDataProviderTypes' */
/** @import { OptionType } from '../../Form/Select/SelectTypes' */
/** @import { FisaAccountClosureProps, FisaCoolingOffStep, FisaCoolingOffPayoutDestination, CloseAccountRequest, FisaOtpSuccessHandler, CoolingOffPeriodStatus } from './FISA/FisaAccountClosureTypes' */
/** @import { WithFeatureFlagsProps } from '../../FeatureFlags/FeatureFlagsTypes' */

// GraphQL mutation for closing account (cooling-off flow)
const CLOSE_ACCOUNT_WITHIN_COOLING_OFF_PERIOD = gql`
    mutation CloseAccountWithinCoolingOffPeriod($request : CloseAccountWithinCoolingOffPeriodRequestType!){
        closeAccountWithinCoolingOffPeriod(request:$request) {
            success
        }
    }
`;

// GraphQL mutation for closing account with penalty (penalty flow)
const CLOSE_ACCOUNT_WITH_PENALTY = gql`
    mutation CloseAccountWithPenalty($request : CloseAccountWithPenaltyRequestType!){
        closeAccountWithPenalty(request:$request) {
            success
        }
    }
`;

/**
 * FISA Account Closure Component
 * @param {FisaAccountClosureProps & WithFeatureFlagsProps} _props
 */

function FisaAccountClosure(/** @type {FisaAccountClosureProps & WithFeatureFlagsProps} */ { featureEnabled }) {
    const accountIdFromUrl = window.location.pathname.split('/').pop();
    const [fisaAccount, setFisaAccount] = useState(/** @type {Account | undefined} */(undefined));
    const navigate = useNavigate();
    const data = useContext(customerDataContext)?.data;
    const analytics = useUnifiedAnalytics();

    // Step and form state lifted here
    const [step, setStep] = useState(/** @type {FisaCoolingOffStep} */(1));
    const [reason, setReason] = useState(/** @type {OptionType | null} */(null));
    const [freeTextReason, setFreeTextReason] = useState('');
    const [payoutDestination, setPayoutDestination] = useState(/** @type {FisaCoolingOffPayoutDestination} */('linked-account'));
    const [payoutDestinationAccount, setPayoutDestinationAccount] = useState(/** @type {OptionType | null} */(null));
    const [_isClosing, setIsClosing] = useState(false);
    const [completionToken, setCompletionToken] = useState('');
    const [closeAccountWithinCoolingOffPeriod] = useMutation(CLOSE_ACCOUNT_WITHIN_COOLING_OFF_PERIOD);
    const [closeAccountWithPenalty] = useMutation(CLOSE_ACCOUNT_WITH_PENALTY);
    const [_errorMsg, setErrorMsg] = useState('');
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const hasEligibleDisaAccount = data?.customer?.accounts?.some(account =>
        account.product?.productType === 'INSTANT_ACCESS_CASH_ISA' &&
        ['', 'Active', 'FundingOpen'].includes(account.accountStatus ?? '')
    );
    const eligibleDisaAccountOptions = [
        ...(data?.customer?.accounts
        ?.filter(account =>
            account.product?.productType === 'INSTANT_ACCESS_CASH_ISA' &&
            ['', 'Active', 'FundingOpen'].includes(account.accountStatus ?? '')
        )
        .map(account => ({
            label: `${account.product?.name || 'Instant Access Cash ISA'} (${account.accountNumber || ''})`,
            value: account.id
        })) || []),
    ];
    const isFisaMaturityEnabled = featureEnabled('EnableFisaMaturity');
    const shouldShowPayoutDestinations = isFisaMaturityEnabled && hasEligibleDisaAccount;

    // Additional state for penalty notice closure data (including penaltyAmount)
    const [penaltyClosureData, setPenaltyClosureData] = useState(/** @type {any} */(null));

    // Handler to receive closure data from penalty notice component
    const handlePenaltyClosureDataChange = (/** @type {any} */ closureData) => {
        setPenaltyClosureData(closureData);
    };

    // Analytics-enabled onContinue handler
    const handleContinueClick = () => {
        analytics.trackAnalyticsClickEvent(
            'fisa cooling off closure continue',
            window.location.href,
            'fisa-closure',
            'fisa_1yr',
            '',
            {},
            'button click',
            'close_fisa_account|button_click|continue',
            'account_closure_cooling_off'
        );
        setStep(2);
    };

    /**
     * Check if the account is within the cooling-off period
     * @param {Date | string | undefined} coolingOffEndDate
     * @returns {CoolingOffPeriodStatus}
     */
    const checkCoolingOffPeriod = (coolingOffEndDate) => {
        if (!coolingOffEndDate) {
            return { isWithinCoolingOffPeriod: false };
        }

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const coolingOffEnd = new Date(new Date(coolingOffEndDate).getFullYear(), new Date(coolingOffEndDate).getMonth(), new Date(coolingOffEndDate).getDate());
        const isWithinPeriod = today <= coolingOffEnd;
        
        if (isWithinPeriod) {
            const timeDiff = coolingOffEnd.getTime() - today.getTime();
            const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));
            return {
                isWithinCoolingOffPeriod: true,
                coolingOffEndDate: coolingOffEnd,
                daysRemaining: Math.max(0, daysRemaining)
            };
        }

        return {
            isWithinCoolingOffPeriod: false,
            coolingOffEndDate: coolingOffEnd
        };
    };

    useEffect(() => {
        if (data && data.customer && data.customer.accounts) {
            const foundAccount = data.customer.accounts.find(account => account.id === accountIdFromUrl);
            if (foundAccount) {
                // Check if account is already closed or in closure progress
                if (foundAccount.accountStatus === 'Closed' || foundAccount.accountStatus === 'ClosureRequested') {
                    // Redirect to account details or home page
                    navigate('/');
                    return;
                }
                
                // Log cooling off period information for debugging
                const coolingOffStatus = checkCoolingOffPeriod(foundAccount.coolingOffEndDate);
                
                setFisaAccount(foundAccount);
            } else {
                // Account not found or user doesn't have access - redirect to home
                navigate('/');
            }
        }
    }, [accountIdFromUrl, data, navigate]);

    const goBack = () => {
        try {
            navigate(-1);
        } catch (error) {
            window.location.href = '/';
        }
    }

    // Function to handle OTP modal opening
    const handleOpenOtpModal = () => {
        setShowOtpModal(true);
    };

    if (!data) {
        return <Loading message="Loading..."/>;
    }

    // Step rendering function
    const renderCoolingOffStep = () => {
        if (step === 1) {
            return (
                <FisaCoolingOffClosureStepOne
                    reason={reason}
                    setReason={setReason}
                    freeTextReason={freeTextReason}
                    setFreeTextReason={setFreeTextReason}
                    payoutDestination={payoutDestination}
                    setPayoutDestination={destination => {
                        setPayoutDestination(destination);
                        if (destination === 'linked-account') setPayoutDestinationAccount(null);
                    }}
                    showPayoutDestinations={shouldShowPayoutDestinations}
                    eligibleDisaAccountOptions={eligibleDisaAccountOptions}
                    payoutDestinationAccount={payoutDestinationAccount}
                    setPayoutDestinationAccount={setPayoutDestinationAccount}
                    onContinue={handleContinueClick}
                    onCancel={goBack}
                />
            );
        }
        if (step === 2) {
            return (
                <FisaCoolingOffClosureStepTwo
                    finalPaymentAmount={fisaAccount ? fisaAccount.balance : ''}
                    interestAccrued={fisaAccount ? fisaAccount.interestAccrued : undefined}
                    payoutDestination={payoutDestination}
                    showLinkedAccountPayoutWarning={isFisaMaturityEnabled && payoutDestination === 'linked-account'}
                    onCloseAccount={handleOpenOtpModal}
                    onCancel={() => {
                        if (fisaAccount && fisaAccount.id) {
                            navigate(`/account-details/${fisaAccount.id}`);
                        } else {
                            goBack();
                        }
                    }}
                />
            );
        }
        return null;
    };

    // Penalty notice steps rendering function
    const renderPenaltyNoticeSteps = () => {
        return <FisaPenaltyNoticeStepsOrchestrator 
            fisaAccount={fisaAccount || {}} 
            onCloseAccount={handleOpenOtpModal}
            onClosureDataChange={handlePenaltyClosureDataChange}
        />;
    };

    // Modal rendering for OTP and Success
    const renderModal = () => {
        return (
            <>
                {showOtpModal && (
                    <Modal className="modal modal--fixed-height modal--wide inactivity-modal modal__mobile-optimised" onClose={() => setShowOtpModal(false)} mobileOptimised>
                        <h2 className="modal__title">Verify account closure</h2>
                        <SmsOtp
                            mobileNumber={data?.customer?.mobileNumber || ''}
                            onSuccess={(_e, _m, token) => handleOtpSuccess(token || '')}
                            analyticsRegion={"account details"}
                            analyticsAction={"account closure"}
                        />
                    </Modal>
                )}
                {showSuccessModal && (
                    <Modal className="modal modal--fixed-height modal--wide inactivity-modal modal__mobile-optimised" onClose={() => {
                        setShowSuccessModal(false);
                        if (fisaAccount && fisaAccount.id) {
                            navigate(`/`);
                        } else {
                            goBack();
                        }
                    }} mobileOptimised>
                        <div className="modal__spacer--small" />
                        <img src={successImg} className='account-activity__image' alt="Success" />
                        <h4 className="modal__title-closure">Thank you</h4>
                        <p className="modal__text-closure">Your account is scheduled to close.</p>
                        <div className="modal__spacer--small" />
                        <Button size='med' customClassNames={["modal__button"]} label="OK" onClick={() => {
                            setShowSuccessModal(false);
                            if (fisaAccount && fisaAccount.id) {
                                navigate(`/`);
                            } else {
                                goBack();
                            }
                        }} />
                    </Modal>
                )}
            </>
        );
    };

    // Call close account mutation after OTP
    /** @type {FisaOtpSuccessHandler} */
    const handleOtpSuccess = async (token) => {
        setIsClosing(true);
        setErrorMsg('');
        setCompletionToken(token);
        try {
            // Determine closure reason based on which flow is being used
            let closureReason = '';
            let additionalClosureReason = '';
            let penaltyAmount = 0;
            let result;
            
            if (penaltyClosureData) {
                // Using penalty notice flow data
                const data = /** @type {any} */ (penaltyClosureData);
                closureReason = (data['closureReason'] && data['closureReason']['value']) || data['selectedReason'] || '';
                additionalClosureReason = data['freeTextReason'] || '';
                penaltyAmount = data['penaltyAmount'] || 0;
                // Use the new penalty mutation
                const request = {
                    accountId: fisaAccount?.id || '',
                    completionToken: token || completionToken,
                    closureReason: closureReason,
                    additionalClosureReason: additionalClosureReason,
                    penaltyAmount: penaltyAmount
                };
                result = await closeAccountWithPenalty({
                    variables: { request }
                });
            } else {
                // Using cooling-off flow data
                closureReason = reason?.value || '';
                additionalClosureReason = freeTextReason || '';
                const request = {
                    accountId: fisaAccount?.id || '',
                    completionToken: token || completionToken,
                    closureReason: closureReason,
                    additionalClosureReason: additionalClosureReason,
                    payoutDestinationAccountId: payoutDestination === 'instant-access-cash-isa'
                        ? payoutDestinationAccount?.value || null
                        : null
                };
                result = await closeAccountWithinCoolingOffPeriod({
                    variables: { request }
                });
            }
            
            // Check success based on which mutation was used
            const isSuccess = penaltyClosureData 
                ? result?.data?.closeAccountWithPenalty?.success 
                : result?.data?.closeAccountWithinCoolingOffPeriod?.success;
                
            if (isSuccess) {
                // Track analytics for successful OTP verification and account closure
                const featureName = penaltyClosureData ? "account_closure_penalty" : "account_closure_cooling_off";
                analytics.trackAnalyticsClickEvent(
                    "we've confirmed your account closure",
                    window.location.href,
                    "fisa-closure",
                    "fisa_1yr",
                    "",
                    {},
                    "roadblock load",
                    "close_fisa_account|roadblock_load|account_closure_confirmed",
                    featureName
                );
                
                setShowOtpModal(false);
                setShowSuccessModal(true);
            } else {
                setErrorMsg('Something went wrong. Please try again.');
            }
        } catch (_err) {
            setErrorMsg('Something went wrong. Please try again.');
        }
        setIsClosing(false);
    };

    return (
        <>
            <div className="fisa-account-closure">
                <div className="fisa-account-closure__flex-row">
                    <img src={logoImg} alt="Investec"/>
                    <CloseButton onClick={goBack}/>
                </div>
                <div className="fisa-account-closure__container text__navy fisa-account-closure fisa-account-closure-form-container">
                    <h1 className="fisa-account-closure__title fisa-account-closure__span_content2">Close your {fisaAccount ? fisaAccount.product.name : ''}</h1>
                    <div className="fisa-account-closure__flex-row fisa-account-closure__flex-row--responsive">
                        {fisaAccount && <FisaAccountDetailsBox fisaAccount={fisaAccount} />}                    
                    </div>
                    {fisaAccount && checkCoolingOffPeriod(fisaAccount.coolingOffEndDate).isWithinCoolingOffPeriod 
                        ? renderCoolingOffStep() 
                        : renderPenaltyNoticeSteps()
                    }
                </div>
            </div>
            {renderModal()}
        </>
    );
}

export default withFeatureFlags(FisaAccountClosure);

